import { Injectable, inject } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { TimeService } from '@services/time-service';
import { QuizQuestionEntry } from '@services/experiment/experiment.service.types';

const HAIR_TOP = 'HAIR_TOP';
const HAIR_FRONT = 'HAIR_FRONT';

const ENGLISH = 'en';
const MIN_AGE_FOR_PREGNANCY_QUESTION = 16;

@Injectable({ providedIn: 'root' })
/** Evaluates a variant entry's gate. Photo gates are inverted — see the engine's CLAUDE.md. */
export class QuizGateService {
  private readonly conn = inject(ConnectionService);
  private readonly experimentService = inject(ExperimentService);
  private readonly timeService = inject(TimeService);

  private recentCheckupByType = new Map<string, Promise<{ imagePath?: string } | undefined>>();

  /** Called by the engine at the start of every load, so a capture made while away is always seen. */
  resetCache(): void {
    this.recentCheckupByType = new Map();
  }

  async shouldRender(entry: QuizQuestionEntry): Promise<boolean> {
    switch (entry.gate) {
      case undefined:
      case null:
        return true;
      case 'evaluateAgeMoreThan17':
        return this.isAboveMinAge();
      case 'evaluateTakenInstantCheckupRecentlyHairTop':
        return !(await this.hasRecentInstantCheckup(HAIR_TOP));
      case 'evaluateTakenInstantCheckupRecentlyHairFront':
        return !(await this.hasRecentInstantCheckup(HAIR_FRONT));
      case 'isLanguagePreferenceEnglish':
        return this.isLanguagePreferenceEnglish();
      default:
        return true;
    }
  }

  private findRecentCheckup(type: string): Promise<{ imagePath?: string } | undefined> {
    const cached = this.recentCheckupByType.get(type);
    if (this.recentCheckupByType.has(type)) return cached;
    const request = (async (): Promise<{ imagePath?: string } | undefined> => {
      const user = this.conn.getCurrentUser();
      if (!user) return undefined;
      const startDate = this.timeService.removeDays(new Date(), this.experimentService.getInstantCheckupRecencyDays());
      const [checkup] = await this.conn.fetchInstantCheckup({
        dateRange: { endDate: new Date(), startDate },
        limit: 1,
        project: ['type', 'imagePath'],
        type: [type],
        userId: user.id,
      });
      return checkup;
    })();
    this.recentCheckupByType.set(type, request);
    return request;
  }

  private isAboveMinAge(): boolean {
    const age = Number(this.conn.getCurrentUser()?.get('Age')) || 0;
    return age > MIN_AGE_FOR_PREGNANCY_QUESTION;
  }

  private isLanguagePreferenceEnglish(): boolean {
    return this.conn.getCurrentUser()?.get('languagePreference') === ENGLISH;
  }

  /** Mirrors the tree's hasUserTakenInstantCheckupRecently, with the window tunable from the variant. */
  private async hasRecentInstantCheckup(type: string): Promise<boolean> {
    return !!await this.findRecentCheckup(type);
  }
}
